
    <!-- Single Toast -->
    @if (showSingle()) {
    <div [class]="computedToastClasses()" [attr.role]="ariaRole" [attr.aria-live]="ariaLive">
      <div class="relative z-10 p-4">
        <div class="flex items-start space-x-3">
          <!-- Icon -->
          @if (icon()) {
          <div [class]="iconContainerClasses()">
            <ui-icon [name]="icon()" size="md" [customClass]="iconClasses()"></ui-icon>
          </div>
          }

          <!-- Content -->
          <div class="flex-1 min-w-0">
            @if (title) {
            <h4 class="mb-1 truncate text-sm font-semibold text-white">
              {{ title }}
            </h4>
            }
            <p class="line-clamp-3 text-sm text-slate-200">
              {{ message }}
            </p>

            <!-- Actions -->
            @if (actions && actions.length > 0) {
            <div class="flex items-center space-x-2 mt-3">
              @for (action of actions; track action.label) {
              <button
                (click)="handleActionClick(action)"
                [class]="getActionClasses(action)"
                class="text-xs font-medium px-3 py-1 rounded-lg transition-all duration-200
                             focus:ring-2 focus:ring-offset-2 focus:outline-none"
              >
                {{ action.label }}
              </button>
              }
            </div>
            }
          </div>

          <!-- Close Button -->
          @if (dismissible) {
          <button
            (click)="dismiss()"
            class="relative z-20 rounded-xl border border-blue-200/14 bg-white/6 p-1.5 transition-all duration-200 hover:bg-white/12
                       focus:ring-2 focus:ring-primary-500/50 focus:outline-none
                       text-slate-300 hover:text-white"
            aria-label="Close notification"
          >
            <ui-icon name="x" size="sm"></ui-icon>
          </button>
          }
        </div>

        <!-- Progress bar (for auto-dismiss) -->
        @if (showProgress && duration > 0) {
        <div
          class="absolute bottom-0 left-0 right-0 h-1 overflow-hidden rounded-b-2xl bg-white/8"
        >
          <div
            class="h-full bg-linear-to-r from-primary-500 to-primary-600 transition-all duration-100 ease-linear"
            [style.width.%]="progressWidth()"
          ></div>
        </div>
        }
      </div>

      <!-- Glass shimmer effect -->
      <div
        class="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none overflow-hidden rounded-xl"
      >
        <div
          class="absolute inset-0 bg-linear-to-r from-transparent via-primary-400/10 to-transparent
                      transform -skew-x-12 -translate-x-full hover:translate-x-full
                      transition-transform duration-1500 ease-out"
        ></div>
      </div>

      <!-- Premium floating particles -->
      @if (variant === 'premium') {
      <div class="absolute inset-0 overflow-hidden pointer-events-none opacity-20 rounded-xl">
        <div class="absolute top-1/4 left-1/6 w-1 h-1 bg-primary-400/60 rounded-full animate-pulse"></div>
        <div class="absolute bottom-1/3 right-1/4 w-0.5 h-0.5 bg-white/40 rounded-full animate-pulse delay-1000"></div>
        <div
          class="absolute top-3/4 left-3/4 w-1.5 h-1.5 bg-primary-300/50 rounded-full animate-pulse delay-2000"
        ></div>
      </div>
      }

      <!-- Variant glow effect -->
      @if (variant !== 'default') {
      <div class="absolute inset-0 rounded-xl opacity-20 pointer-events-none">
        <div [class]="glowClasses()"></div>
      </div>
      }
    </div>
    }

    <!-- Toast Container (for service-managed toasts) -->
    @if (!showSingle()) {
    <div [class]="computedContainerClasses()">
      @for (toast of toasts(); track toast.id) {
      <div [class]="getToastClasses(toast)" [attr.role]="getToastAriaRole(toast)" [attr.aria-live]="getToastAriaLive(toast)">
        <div class="relative z-10 p-4">
          <div class="flex items-start space-x-3">
            <!-- Icon -->
            @if (getToastIcon(toast)) {
            <div [class]="getToastIconContainerClasses(toast)">
              <ui-icon [name]="getToastIcon(toast)" size="md" [customClass]="getToastIconClasses(toast)"></ui-icon>
            </div>
            }

            <!-- Content -->
            <div class="flex-1 min-w-0">
              @if (toast.title) {
              <h4 class="mb-1 truncate text-sm font-semibold text-white">
                {{ toast.title }}
              </h4>
              }
              <p class="line-clamp-3 text-sm text-slate-200">
                {{ toast.message }}
              </p>

              <!-- Actions -->
              @if (toast.actions && toast.actions.length > 0) {
              <div class="flex items-center space-x-2 mt-3">
                @for (action of toast.actions; track action.label) {
                <button
                  (click)="handleToastActionClick(action, toast)"
                  [class]="getToastActionClasses(action, toast)"
                  class="text-xs font-medium px-3 py-1 rounded-lg transition-all duration-200
                                 focus:ring-2 focus:ring-offset-2 focus:outline-none"
                >
                  {{ action.label }}
                </button>
                }
              </div>
              }
            </div>

            <!-- Close Button -->
            @if (toast.dismissible !== false) {
            <button
              (click)="dismissToast(toast.id!)"
              class="relative z-20 rounded-xl border border-blue-200/14 bg-white/6 p-1.5 transition-all duration-200 hover:bg-white/12
                           focus:ring-2 focus:ring-primary-500/50 focus:outline-none
                           text-slate-300 hover:text-white"
              aria-label="Close notification"
            >
              <ui-icon name="x" size="sm"></ui-icon>
            </button>
            }
          </div>
        </div>

        <!-- Glass effects (same as single toast) -->
        <div
          class="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none overflow-hidden rounded-xl"
        >
          <div
            class="absolute inset-0 bg-linear-to-r from-transparent via-primary-400/10 to-transparent
                          transform -skew-x-12 -translate-x-full hover:translate-x-full
                          transition-transform duration-1500 ease-out"
          ></div>
        </div>

        @if (toast.variant === 'premium') {
        <div class="absolute inset-0 overflow-hidden pointer-events-none opacity-20 rounded-xl">
          <div class="absolute top-1/4 left-1/6 w-1 h-1 bg-primary-400/60 rounded-full animate-pulse"></div>
          <div
            class="absolute bottom-1/3 right-1/4 w-0.5 h-0.5 bg-white/40 rounded-full animate-pulse delay-1000"
          ></div>
          <div
            class="absolute top-3/4 left-3/4 w-1.5 h-1.5 bg-primary-300/50 rounded-full animate-pulse delay-2000"
          ></div>
        </div>
        } @if (toast.variant && toast.variant !== 'default') {
        <div class="absolute inset-0 rounded-xl opacity-20 pointer-events-none">
          <div [class]="getToastGlowClasses(toast)"></div>
        </div>
        }
      </div>
      }
    </div>
    }
  
